import React, { createContext, useEffect, useReducer, useContext } from "react";

import { AccountActionType } from "../store/actions";
import apiServices, { setSessionToken, clearSessionToken, getSessionToken } from "../services/apiServices";
import accountReducer, { UserInterface, AccountState } from "../store/accountReducer";
import Loader from "../components/Loader/Loader";
import { SessionContext } from "./SessionContext";

const initialState: AccountState = {
   isLoggedIn: false,
   isInitialised: false,
   user: null,
};

const JWTContext = createContext({
   ...initialState,
   requestMagicLink: (email: string) => Promise.resolve({ message: "" }),
   verifyMagicLink: (token: string) => Promise.resolve(),
   logout: () => {},
});

export const JWTProvider: React.FC<{
   children?: React.ReactNode;
}> = ({ children }) => {
   const [state, dispatch] = useReducer(accountReducer, initialState);
   const session = useContext(SessionContext);

   const requestMagicLink = async (email: string): Promise<{ message: string }> => {
      const response = await apiServices.post("/users/magic-link", { email });
      return response.data;
   };

   const verifyMagicLink = async (token: string) => {
      const response = await apiServices.post("/users/verify-magic-link", { token });
      const { user, token: sessionToken }: { user: UserInterface; token: string } = response.data;
      setSessionToken(sessionToken);
      const domain = user.domains[0];
      await session.switchDomain(domain);
      dispatch({
         type: AccountActionType.LOGIN,
         payload: {
            user,
         },
      });
   };

   const logout = () => {
      clearSessionToken();
      apiServices.post("/users/me/logout").catch(() => {});
      dispatch({ type: AccountActionType.LOGOUT });
   };

   useEffect(() => {
      const init = async () => {
         if (!getSessionToken()) {
            dispatch({
               type: AccountActionType.ACCOUNT_INITIALISE,
               payload: {
                  isLoggedIn: false,
                  user: null,
               },
            });
            return;
         }
         try {
            const response = await apiServices.get("/users/me");
            const { user } = response.data;
            const domain = user.domains[0];
            await session.switchDomain(domain);
            dispatch({
               type: AccountActionType.ACCOUNT_INITIALISE,
               payload: {
                  isLoggedIn: true,
                  user,
               },
            });
         } catch (err) {
            clearSessionToken();
            dispatch({
               type: AccountActionType.ACCOUNT_INITIALISE,
               payload: {
                  isLoggedIn: false,
                  user: null,
               },
            });
         }
      };

      init();
      // eslint-disable-next-line
   }, []);

   if (!state.isInitialised) {
      return <Loader />;
   }

   return <JWTContext.Provider value={{ ...state, requestMagicLink, verifyMagicLink, logout }}>{children}</JWTContext.Provider>;
};

export default JWTContext;
